import * as React from "react";
import type { ColumnDefinition, ColumnFilterOption } from "../../../ui/components/Table/types";
import * as s from "../../../ui/components/Table/Table.css";
import { PRODUCT_LINE_LABELS } from "../../lib/api/products";
import type { ProductLine, ProductListItem, ProductStatus } from "../../lib/api/products";
import { DeviceHealthIndicator } from "../../../ui/components/DeviceHealthIndicator";
import { OrgThumbnail } from "../../../ui/components/OrgThumbnail";
import { resolveLastSignalAt } from "../../../ui/components/DeviceHealthIndicator/resolveLastSignalAt";
import {
  ActiveAlertsCountBadge,
  type ActiveAlertSummary,
} from "./ActiveAlertsCountBadge";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATUS_COLORS: Record<ProductStatus, { color: string; label: string }> = {
  active: { color: "#16A34A", label: "Active" },
  inactive: { color: "#6B7280", label: "Inactive" },
  decommissioned: { color: "#991B1B", label: "Decommissioned" },
};

const PRODUCT_LINE_FILTER_OPTIONS: ColumnFilterOption[] = (
  Object.entries(PRODUCT_LINE_LABELS) as Array<[ProductLine, string]>
).map(([value, label]) => ({ value, label }));

export interface ProductFilterOptions {
  organizations: ColumnFilterOption[];
  models: ColumnFilterOption[];
}

/**
 * Factory that injects refs to the live telemetry signal maps so the health column
 * can show real-time status without the column definitions changing identity on every
 * WS update (which would trigger a full list refetch each time).
 */
export function createProductsTableColumns(
  lastTelemetryAtMapRef: React.RefObject<Map<string, string>>,
  lastShadowAtMapRef: React.RefObject<Map<string, string>>,
  serviceStartedAtRef: React.RefObject<number | undefined>,
  activeAlertsMapRef?: React.RefObject<Map<string, ActiveAlertSummary[]>>,
  onOrganizationClick?: (organizationId: string) => void,
  filterOptionsRef?: React.RefObject<ProductFilterOptions>,
): ColumnDefinition<ProductListItem>[] {
  return [
    {
      id: "health",
      label: "Connection",
      headerLabelIcon: "wifi",
      width: 44,
      pinnedLeft: true,
      enableHiding: false,
      render: (row) => {
        const telemetryAt = lastTelemetryAtMapRef.current?.get(row.id) ?? row.lastTelemetryAt ?? null;
        const shadowAt = lastShadowAtMapRef.current?.get(row.id) ?? row.lastShadowAt ?? null;
        const signalAt = resolveLastSignalAt(row.lastSignalAt, telemetryAt, shadowAt);
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <DeviceHealthIndicator
              lastSignalAt={signalAt}
              lastTelemetryAt={telemetryAt}
              lastShadowAt={shadowAt}
              productCreatedAt={row.createdAt}
              serviceStartedAt={serviceStartedAtRef.current ?? undefined}
              isSimulated={row.isSimulated}
              isSimulating={row.isSimulating}
              simulationStartedAt={row.simulationStartedAt}
              simulationExpAt={row.simulationExpAt}
              size="sm"
            />
          </div>
        );
      },
    },
    {
      id: "name",
      label: "Name",
      width: 240,
      sortable: true,
      sortKey: "name",
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (row) => {
        const alerts = activeAlertsMapRef?.current?.get(row.id) ?? [];
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className={s.cellTextPrimary}>{row.name}</span>
            {row.isSimulated && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                padding: "1px 6px", borderRadius: 4,
                background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE",
              }}>
                SIM
              </span>
            )}
            {alerts.length > 0 ? <ActiveAlertsCountBadge alerts={alerts} /> : null}
          </span>
        );
      },
    },
    {
      id: "productLine",
      label: "Product Line",
      width: 140,
      enableHiding: true,
      defaultHidden: true,
      filterKey: "product_lines",
      filterLabel: "Product Line",
      filterType: "checkbox",
      filterOptions: () => PRODUCT_LINE_FILTER_OPTIONS,
      filterAccessor: (row) => row.productModel?.productLine ?? "",
      render: (row) => {
        const line = row.productModel?.productLine;
        return (
          <span className={s.cellTextSecondary}>
            {line ? PRODUCT_LINE_LABELS[line] ?? line : "—"}
          </span>
        );
      },
    },
    {
      id: "productModel",
      label: "Model",
      width: 180,
      enableHiding: true,
      filterKey: "product_model_ids",
      filterLabel: "Model",
      filterType: "checkbox",
      showFilterByDefault: false,
      filterOptions: () => filterOptionsRef?.current?.models ?? [],
      filterAccessor: (row) => row.productModelId ?? "",
      render: (row) => (
        <span className={s.cellTextSecondary}>
          {row.productModel?.name ?? "—"}
        </span>
      ),
    },
    {
      id: "organization",
      label: "Organization",
      width: 180,
      enableHiding: true,
      filterKey: "organization_ids",
      filterLabel: "Organizations",
      filterType: "checkbox",
      showFilterByDefault: false,
      filterOptions: () => filterOptionsRef?.current?.organizations ?? [],
      filterAccessor: (row) => row.access?.owningOrganization?.id ?? "",
      render: (row) => {
        const org = row.access?.owningOrganization;
        if (!org?.name) return <span className={s.cellTextSecondary}>—</span>;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <OrgThumbnail
              name={org.name}
              logoUrl={org.logoUrl}
              size={20}
              onClick={onOrganizationClick ? () => onOrganizationClick(org.id) : undefined}
            />
            <span className={s.cellTextSecondary}>{org.name}</span>
          </span>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      width: 140,
      sortable: true,
      sortKey: "status",
      filterKey: "status",
      filterLabel: "Status",
      filterType: "checkbox",
      filterOptions: () => [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "decommissioned", label: "Decommissioned" },
      ],
      filterAccessor: (row) => row.status,
      render: (row) => {
        if (row.status === 'active') return null;
        const { color, label } = STATUS_COLORS[row.status as ProductStatus] ?? STATUS_COLORS.inactive;
        return (
          <span className={s.cellTextSecondary} style={{ color, fontWeight: 600 }}>
            {label}
          </span>
        );
      },
    },
    {
      id: "serial",
      label: "Serial",
      width: 160,
      enableHiding: true,
      defaultHidden: true,
      render: (row) => (
        <span className={s.cellTextSecondary}>{row.serial ?? "—"}</span>
      ),
    },
    {
      id: "assetTag",
      label: "Asset Tag",
      width: 140,
      enableHiding: true,
      defaultHidden: true,
      render: (row) => (
        <span className={s.cellTextSecondary}>{row.assetTag ?? "—"}</span>
      ),
    },
    {
      id: "location",
      label: "Location",
      width: 180,
      enableHiding: true,
      render: (row) => {
        const parts = [row.city, row.state].filter(Boolean);
        return (
          <span className={s.cellTextSecondary}>
            {parts.length > 0 ? parts.join(", ") : "—"}
          </span>
        );
      },
    },
    {
      id: "isSimulated",
      label: "Simulated",
      width: 100,
      enableHiding: true,
      defaultHidden: true,
      render: (row) => (
        <span className={s.cellTextSecondary}>{row.isSimulated ? "Yes" : "No"}</span>
      ),
    },
    {
      id: "createdAt",
      label: "Created",
      width: 140,
      sortable: true,
      sortKey: "created_at",
      defaultHidden: true,
      enableHiding: true,
      render: (row) => (
        <span className={s.cellTextSecondaryTabular}>{formatDate(row.createdAt)}</span>
      ),
    },
  ];
}
